import { useState, useContext } from 'react';
import { Box, Alert } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { AppContext, SttDialog, SttForm } from '../../../sporttia/all';
import constants from '../../../config/constants';

type Props = {
    open?: boolean;
    device?: {
        id?: string;
    };
    onClose: () => void;
};

type Form = {
    code: string;
    direction: string;
    date: string;
    time: string;
};

type SimulateResult = {
    error?: string;
};

type SimulateDeviceResult = {
    situation?: {
        mainLightsOn?: boolean;
        cortesyLightsOn?: boolean;
    };
};

export default function SimulateAccessDialog({
    open = false,
    device,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [simulateResult, setSimulateResult] = useState<
        SimulateResult | undefined
    >();
    const [simulateDeviceResult, setSimulateDeviceResult] = useState<
        SimulateDeviceResult | undefined
    >();
    const [form, setForm] = useState<Form>({
        code: '1111',
        direction: 'IN',
        date: format(new Date(), 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm'),
    });

    /**
     * Simulate pass
     */
    const simulatePass = (): void => {
        setSimulateResult(undefined);
        cxt?.api(
            'GET',
            `${constants.boundedContextPrefixes.MANAGER}/devices/${device?.id}/auth`,
            {
                params: {
                    code: form.code,
                    direction: form.direction,
                    moment: `${form.date}T${form.time}:00`,
                },
                success: (r: {
                    access: SimulateResult;
                    device: SimulateDeviceResult;
                }) => {
                    setSimulateResult(r.access);
                    setSimulateDeviceResult(r.device);
                },
            },
        );
    };

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (!name) return;
        setForm({ ...form, [name]: value as string });
    };

    // -----| Render |-----
    return (
        <SttDialog
            title={t('SimulatePass')}
            open={open}
            onClose={onClose}
            content={
                <>
                    <SttForm
                        elements={
                            [
                                {
                                    md: 12,
                                    type: 'textarea',
                                    caption: t('Code'),
                                    name: 'code',
                                    value: form.code,
                                },
                                {
                                    md: 4,
                                    caption: t('Direction'),
                                    name: 'direction',
                                    type: 'select',
                                    value: form.direction,
                                    options: [
                                        {
                                            caption: t('DirectionIn'),
                                            value: 'IN',
                                        },
                                        {
                                            caption: t('DirectionOut'),
                                            value: 'OUT',
                                        },
                                    ] as any,
                                },
                                {
                                    md: 5,
                                    caption: t('Date'),
                                    type: 'date',
                                    name: 'date',
                                    value: form.date,
                                },
                                {
                                    md: 3,
                                    caption: t('Time'),
                                    type: 'time',
                                    name: 'time',
                                    value: form.time,
                                },
                            ] as any
                        }
                        onChangeForm={handleForm}
                    />

                    {/* --- Result --- */}
                    {simulateResult && (
                        <Box mt={2}>
                            <Alert
                                severity={
                                    simulateResult.error ? 'error' : 'success'
                                }
                            >
                                {simulateResult.error
                                    ? simulateResult.error
                                    : 'OK'}
                            </Alert>
                        </Box>
                    )}

                    {simulateDeviceResult &&
                        simulateDeviceResult?.situation && (
                            <Box mt={2}>
                                <Alert
                                    severity={
                                        simulateDeviceResult?.situation
                                            ?.mainLightsOn
                                            ? 'success'
                                            : 'error'
                                    }
                                >
                                    {t('MainLights')}:{' '}
                                    {simulateDeviceResult?.situation
                                        ?.mainLightsOn
                                        ? 'ON'
                                        : 'OFF'}
                                </Alert>
                            </Box>
                        )}

                    {simulateDeviceResult &&
                        simulateDeviceResult?.situation && (
                            <Box mt={2}>
                                <Alert
                                    severity={
                                        simulateDeviceResult?.situation
                                            ?.cortesyLightsOn
                                            ? 'success'
                                            : 'error'
                                    }
                                >
                                    {t('CortesyLights')}:{' '}
                                    {simulateDeviceResult?.situation
                                        ?.cortesyLightsOn
                                        ? 'ON'
                                        : 'OFF'}
                                </Alert>
                            </Box>
                        )}
                </>
            }
            buttons={[
                {
                    type: 'accept',
                    onClick: simulatePass,
                },
            ]}
        />
    );
}
